/**
 * @sunday/choose: Help me choose (mockup v107, rules level B, 16 Oct 2026; result rebuilt to sell, 25 Oct 2026).
 * Six rows on one sheet (age, weight, sleep, feel, anything else, size) and one result: the pick at the
 * visitor's size with the reason it fits, the monthly price big, price · size · per night small, the rating on one
 * line, an optional protector line, "Add <size> to cart" (straight to the cart), the trial, the cheaper option as a
 * plain link, Ask family, Change answers.
 *
 * No words and no rules live here. Everything comes from the Sunday dock and sheets settings, printed by
 * snippets/sunday-pill.liquid as script[data-sd-choose-cfg]:
 *  - rules: one per line, "conditions > result | reason", applied in order (each match replaces the pick);
 *    conditions are age= wt= sl= fe= p= top= (comma = any of; != = none of), bp, !bp, hot, !hot;
 *    a result is a mattress code, "+t" adds the topper.
 *  - lower: the same form; the first match is the lower-price option.
 *  - concerns: "conditions > tick | reasons it repeats"; the first that applies and doesn't repeat the
 *    reason is the second tick.
 *  - words: the reasons and ticks, and the result's own words (keys starting x_).
 * Mattresses (names, colours, renders, prices and variant ids by size, warranty years, one review each) come
 * from the records; the protector from the store facts.
 *
 * The answers are kept in this browser (@sunday/state "choose"). When the result shows: the size goes to
 * "bed" (it then follows the visitor to every page), the pick's handle to "mattress", the persona code to
 * "hcPersona" (kept apart from "persona", the Home persona pages' key), and three short cart notes wait in this
 * browser for the cart page (the answers in one line, the pick as SKUs, the persona's name; plus _persona).
 * Other parts read the pick with current(), the persona with persona(), and listen for "sunday:pick".
 */
import * as state from '@sunday/state';
import { parse, cutFrom } from '@sunday/sizes';
import { money, esc, emi, fill } from '@sunday/util';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

let cfg = null;
try {
  cfg = JSON.parse($('script[data-sd-choose-cfg]').textContent);
} catch (e) {
  cfg = null;
}
const T = (cfg && cfg.text) || {};
const W = (cfg && cfg.words) || {};
/* the Home tiles' own order (kept on the first move, so the pick can go first and come back) */
let homeOrder = null;
/* 21 Oct (AR): a landing is the first page only; the result's links go to the regular pages */
/* pair: "See this pair" (mattress + topper) lands with "-pair" after the mark, and the mattress page ticks the topper */
function goUrl(url, pair = false) {
  const u = new URL(url, location.origin);
  return u.pathname + u.search + (cfg && cfg.land ? '#' + cfg.land + (pair ? '-pair' : '') : '');
}
const pairIn = !!(cfg && cfg.land && location.hash === '#' + cfg.land + '-pair');
/** True when this page was opened from "See this pair" (read before the mark is taken off the address). */
export const cameForPair = () => pairIn;

/* ---------- the rules (pure) ---------- */
function lines(src) {
  return String(src || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && l.includes('>'))
    .map((l) => {
      const [lhs, rhs] = l.split('>');
      const [out, why] = rhs.split('|').map((x) => (x || '').trim());
      return { conds: lhs.trim().split(/\s+/).filter(Boolean), out, why };
    });
}
function test(tok, f) {
  if (tok === 'bp' || tok === 'hot') return !!f[tok];
  if (tok === '!bp' || tok === '!hot') return !f[tok.slice(1)];
  const m = /^(\w+)(!?=)(.+)$/.exec(tok);
  if (!m) return false;
  const v = String(f[m[1]] ?? '');
  const hit = m[3].split(',').includes(v);
  return m[2] === '=' ? hit : !hit;
}
const matches = (r, f) => r.conds.every((t) => test(t, f));
const outOf = (s) => ({ p: s.replace(/\+t$/i, ''), top: /\+t$/i.test(s) });

/** The pick for one set of answers. a = { age, wt, sl, fe }, ex = ['bp', 'hot']. */
export function decide(c, a, ex = []) {
  const kid = a.age === c.kid;
  const f = { age: a.age, wt: kid ? c.kidWeight : a.wt, sl: a.sl, fe: a.fe, bp: !kid && ex.includes('bp'), hot: !kid && ex.includes('hot'), p: '', top: '0' };
  let why = '';
  lines(c.rules).forEach((r) => {
    if (!matches(r, f)) return;
    const o = outOf(r.out);
    f.p = o.p;
    f.top = o.top ? '1' : '0';
    why = r.why;
  });
  const lowR = lines(c.lower).find((r) => matches(r, f));
  const alt = lowR ? { ...outOf(lowR.out), why: lowR.why } : null;
  const Wd = c.words || {};
  const t1 = Wd[why] || '';
  let t2 = '';
  for (const r of lines(c.concerns)) {
    if (!matches(r, f)) continue;
    const skip = (r.why || '').split(',').map((x) => x.trim());
    const w = Wd[r.out] || '';
    if (w && !skip.includes(why) && w !== t1) {
      t2 = w;
      break;
    }
  }
  return { p: f.p, top: f.top === '1', why, ticks: [t1, t2].filter(Boolean), alt: alt && { ...alt, ticks: [Wd[alt.why] || ''].filter(Boolean) } };
}

/** The persona for one set of answers: the first line of the Personas setting that applies, or null. */
export function personaOf(c, a, ex = []) {
  const kid = a.age === c.kid;
  const f = { age: a.age, wt: kid ? c.kidWeight : a.wt, sl: a.sl, fe: a.fe, bp: !kid && ex.includes('bp'), hot: !kid && ex.includes('hot'), p: '', top: '0' };
  const r = lines(c.personas).find((x) => matches(x, f));
  return r && /^[a-z0-9-]+$/.test(r.out) ? { code: r.out, name: r.why || r.out } : null;
}

/* ---------- answers ---------- */
const saved = () => {
  const s = state.get('choose');
  const r = s && typeof s === 'object' ? { a: { ...(s.a || {}) }, ex: Array.isArray(s.ex) ? s.ex : [], seen: !!s.seen, low: s.low || '' } : { a: {}, ex: [], seen: false, low: '' };
  return { ...r, a: { ...known(), ...r.a } };
};
/* 24 Oct (AR): choosing the lower price makes it the pick, for these answers only (new answers, new pick).
   low = the answers it was chosen for. eff() is the pick shown everywhere; first = our first pick when switched. */
const keyOf = (a, ex) => JSON.stringify([a.age, a.wt, a.sl, a.fe, a.sz, [...ex].sort()]);
function eff(d, s) {
  if (!d || !d.alt || !s || !s.low || s.low !== keyOf(s.a, s.ex)) return d;
  return { ...d, p: d.alt.p, top: d.alt.top, why: d.alt.why, ticks: d.alt.ticks, alt: null, first: d };
}
/* What the visitor already told us elsewhere (the fit drawings' person, the bed size) pre-selects the same answers. */
function known() {
  const k = {};
  if (!cfg) return k;
  const P = cfg.person || {};
  const p = state.get('person');
  const back = (map, test) => Object.keys(map || {}).find((x) => test(map[x]));
  if (p && p.touched) {
    const sl = back(P.sl, (v) => v === p.pos);
    if (sl) k.sl = sl;
    const age = back(P.age, (v) => String(v) === String(p.age));
    if (age) k.age = age;
    const wt = back(P.wt, (v) => v && v.g === p.g && v.build === p.build);
    if (wt && k.age !== cfg.kid) k.wt = wt;
  }
  const bed = state.get('bed');
  if (bed && !bed.custom && bed.w) {
    const sz = (cfg.sizes || []).find((x) => x.w === Number(bed.w));
    if (sz) k.sz = sz.k;
  }
  /* 25 Oct: an answer saved before a row's choices changed (13–59, 85+) no longer matches a chip: ask it again */
  const ok = cfg.codes || {};
  Object.keys(ok).forEach((row) => {
    if (k[row] && !ok[row].includes(k[row])) delete k[row];
  });
  return k;
}
const isKid = (a) => cfg && a.age === cfg.kid;
const need = (a) => ['age', 'sl', 'fe', 'sz'].concat(isKid(a) ? [] : ['wt']);
const valid = (a, k) => {
  const ok = cfg && cfg.codes && cfg.codes[k];
  return !!a[k] && (!ok || ok.includes(a[k]));
};
const done = (a) => need(a).every((k) => valid(a, k));

const mat = (code) => (cfg ? cfg.mattresses.find((m) => m.code === code) : null);
const sizeOf = (code) => (cfg ? cfg.sizes.find((s) => s.k === code) : null);

/** The answers' size: width from the answer; the visitor's own length when that width has it, else the setting. */
function bedSize(a) {
  const s = sizeOf(a.sz);
  if (!s) return null;
  const bed = state.get('bed');
  const l = bed && !bed.custom && Number(bed.w) === s.w && bed.l ? Number(bed.l) : cfg.len;
  return { w: s.w, l, word: s.label };
}
function priceAt(v, w, l) {
  const sizes = (v || []).map(([title, price]) => parse({ id: 0, title, price })).filter(Boolean);
  const at = sizes.filter((x) => x.w === w && x.l === l).sort((x, y) => x.price - y.price)[0] || cutFrom(sizes, w, l);
  return at ? at.price : 0;
}
/** The variant id at exactly that size (the cheapest height), or 0. */
function variantAt(v, w, l) {
  const sizes = (v || []).map(([title, price, , id]) => ({ ...parse({ id: 0, title, price }), vid: id })).filter((x) => x.w && x.vid);
  const at = sizes.filter((x) => x.w === w && x.l === l).sort((x, y) => x.price - y.price)[0];
  return at ? at.vid : 0;
}
/** The SKU at that size (OP3K78), else the mattress's letters (OP); '' if there are none. */
function skuAt(v, w, l) {
  const sizes = (v || []).map(([title, price, sku]) => ({ ...parse({ id: 0, title, price }), sku })).filter((x) => x.w);
  const at = sizes.filter((x) => x.w === w && x.l === l && x.sku).sort((x, y) => x.price - y.price)[0];
  if (at) return at.sku;
  const any = (v || []).map((x) => x[2]).find(Boolean);
  return any ? (/^[A-Z]+/.exec(any) || [''])[0] : '';
}
function priced(o, sz) {
  const m = mat(o.p);
  if (!m) return null;
  const mp = priceAt(m.v, sz.w, sz.l);
  const tp = o.top && cfg.topper ? priceAt(cfg.topper.v, sz.w, sz.l) : 0;
  return { m, mp, tp, total: mp + tp, name: o.top ? fill(T.pair, { name: m.trim }) : m.name };
}

/** The visitor's pick, or null: { id, code, top, name, short, url, colour }. */
export function current() {
  if (!cfg) return null;
  const s = saved();
  if (!done(s.a)) return null;
  const d = eff(decide(cfg, s.a, s.ex), s);
  const m = mat(d.p);
  return m ? { id: m.id, code: m.code, top: d.top, name: m.name, short: m.short, trim: m.trim, url: m.url, colour: m.colour } : null;
}

/** Where "Your pick" leads: the pick's page, landing on its price and Add (as See this mattress). */
export function pickHref() {
  const p = current();
  return p ? goUrl(p.url, !!p.top) : '';
}

/** The visitor's persona code ('older', 'backpain' …) or ''. */
export function persona() {
  return (cfg && state.get('hcPersona')) || '';
}

/** The cart notes for a finished set of answers, or null. */
function attrsFor(a, ex, low) {
  if (!cfg || !done(a)) return null;
  const d = eff(decide(cfg, a, ex), { a, ex, low });
  const m = mat(d.p);
  const sz = bedSize(a);
  if (!m || !sz) return null;
  const A = cfg.attrs || {};
  const AW = cfg.attrWords || {};
  const kid = isKid(a);
  const w = (row, code) => AW[`${row}.${code}`] || code;
  const pe = personaOf(cfg, a, ex);
  const ans = [w('age', a.age), kid ? '' : w('wt', a.wt), w('sl', a.sl), w('fe', a.fe)].concat(kid ? [] : ex.map((x) => w('ex', x))).filter(Boolean);
  const vals = {};
  (A.drop || []).map((k) => String(k).trim()).filter(Boolean).forEach((k) => (vals[k] = ''));
  if (A.answers) vals[A.answers] = ans.join(' · ');
  if (A.pick) {
    vals[A.pick] = [skuAt(m.v, sz.w, sz.l) || m.name, d.top && cfg.topper ? skuAt(cfg.topper.v, sz.w, sz.l) : ''].filter(Boolean).join(' + ');
    const fm = d.first && mat(d.first.p);
    if (fm && T.switched) vals[A.pick] += ' (' + fill(T.switched, { pick: skuAt(fm.v, sz.w, sz.l) || fm.name }) + ')';
  }
  /* 26 Oct (AR go): the order shows only the readable notes; _persona and _sunday_answers are cleared through the
     "Old cart notes to clear" setting. The persona code comes back from the Persona name (snippets/sunday-head). */
  if (A.persona) vals[A.persona] = pe ? pe.name : '';
  return { vals, code: pe ? pe.code : '' };
}

/* a short tag of what was last written (kept in this browser instead of the whole notes) */
const tag = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

let lastSig = '';

/* 29 Oct (AR go): GA4 events for the survey through the site's own gtag (none without it). No answers, nothing personal. */
const pageType = () => {
  const p = location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
  if (p === '/') return 'home';
  if (/\/products\//.test(p)) return 'product';
  if (/^\/collections(\/|$)/.test(p)) return 'collection';
  if (/^\/cart(\/|$)/.test(p)) return 'cart';
  return 'other';
};
function ga(name, params) {
  try {
    if (typeof window.gtag === 'function') window.gtag('event', name, { ...params, page_type: pageType() });
  } catch (e) {
    /* the survey goes on */
  }
}
let lastFin = '';
/** Writes the notes once per change; force = write even if this browser thinks it already did. */
function writeAttrs(force) {
  const s = saved();
  const out = attrsFor(s.a, s.ex, s.low);
  if (!out) return;
  if (out.code !== state.get('hcPersona')) state.set('hcPersona', out.code);
  if (out.code) document.documentElement.setAttribute('data-sd-persona', out.code);
  else document.documentElement.removeAttribute('data-sd-persona');
  const sig = tag(JSON.stringify(out.vals));
  if (sig === lastSig || (!force && sig === state.get('chosen'))) return;
  lastSig = sig;
  /* 25 Oct (speed): no cart change here (each one costs about 0.65 s on any page). The notes wait in this browser
     and the cart page saves them with Language and Journey in one go, before Checkout (assets/sunday-cart-page.js). */
  state.set('attrs', out.vals);
  state.set('chosen', sig);
  if (window.Sunday) window.Sunday.persona = out.code;
  /* 25 Oct (AR go): the answers and the pick go to the cart as soon as the survey ends (in the background, once per
     change), so a browser that loses them gets them back from the cart (see the restore below). 26 Oct (AR go): the
     readable notes only; the restore reads the answers back from them. */
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  try {
    fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: out.vals }), keepalive: true }).catch(() => {});
  } catch (e) {
    /* the cart page still saves the notes before Checkout */
  }
}

/* 26 Oct (AR go): the answers from the cart's readable notes. Answers line words → codes (Cart note words setting);
   the size from the pick's first SKU; a pick with "(…)" was switched to the lower price. */
function fromNotes(n) {
  if (!n || !n.answers) return null;
  const back = {};
  Object.entries(cfg.attrWords || {}).forEach(([k, v]) => (back[String(v).trim()] = k));
  const a = {};
  const ex = [];
  String(n.answers).split('·').forEach((w) => {
    const k = back[w.trim()];
    if (!k) return;
    const [row, code] = k.split('.');
    if (row === 'ex') ex.push(code);
    else a[row] = code;
  });
  const sku = (String(n.pick || '').trim().split(/[\s+(]/)[0] || '').trim();
  for (const m of cfg.mattresses || []) {
    const v = sku && (m.v || []).find((x) => x[2] === sku);
    if (!v) continue;
    const z = parse({ id: 0, title: v[0], price: v[1] });
    const sz = z && (cfg.sizes || []).find((x) => x.w === z.w);
    if (sz) a.sz = sz.k;
    break;
  }
  return { a, ex, low: /\(/.test(String(n.pick || '')) ? keyOf(a, ex) : '' };
}

/* 25 Oct (AR go): this browser lost the answers but the cart kept them: write them back here once */
if (cfg && (cfg.cartAnswers || (cfg.cartNotes && cfg.cartNotes.answers)) && !done(saved().a)) {
  try {
    /* Liquid prints cart notes HTML-escaped (&quot;): undo that first */
    const un = (x) => { const raw = document.createElement('textarea'); raw.innerHTML = String(x || ''); return raw.value; };
    /* carts from before 26 Oct still carry _sunday_answers (cleared on the next save) */
    const k = cfg.cartAnswers ? JSON.parse(un(cfg.cartAnswers)) : fromNotes({ answers: un(cfg.cartNotes.answers), pick: un(cfg.cartNotes.pick) });
    if (k && k.a && typeof k.a === 'object') {
      state.set('choose', { a: k.a, ex: Array.isArray(k.ex) ? k.ex : [], seen: true, low: k.low || '' });
      if (done(saved().a)) state.set('chosen', tag(JSON.stringify((attrsFor(saved().a, saved().ex, saved().low) || {}).vals || {})));
    }
  } catch (e) {
    /* keep going without them */
  }
}

/* the cart lost the persona (or never had it) but this browser has the answers: write it again, once */
if (cfg) {
  const s = saved();
  const out = done(s.a) ? attrsFor(s.a, s.ex, s.low) : null;
  const inCart = (window.Sunday && window.Sunday.persona) || '';
  /* answers saved before the keys were split left their code in the Home persona key: take it out once */
  if (out && out.code && !state.get('hcPersona') && state.get('persona') === out.code) state.set('persona', null);
  /* 26 Oct (AR go): notes kept in this browser in an older shape (with _persona), or a cart still holding _persona or
     _sunday_answers, are written again once, in the new shape (the old notes are cleared) */
  const kept = state.get('attrs');
  if (out && ((out.code !== inCart && !kept) || (kept && JSON.stringify(kept) !== JSON.stringify(out.vals)) || cfg.cartOld)) writeAttrs(true);
}

/* ---------- the sheet ---------- */
const root = cfg ? $('[data-sd-hc]') : null;

if (root && cfg) {
  const sheet = $('[data-hc-sheet]', root);
  const dim = $('[data-hc-dim]', root);
  const q = $('[data-hc-q]', root);
  const res = $('[data-hc-r]', root);
  const pill = $('[data-hc-pill]', root);
  const html = document.documentElement;
  let open = false;
  let scr = 'q';
  let miss = false;
  let from = null;
  let prot = false;
  let autoAt = 0;
  const wide = () => matchMedia('(min-width: 960px)').matches;

  const answers = () => saved();
  const save = (patch) => state.set('choose', { ...answers(), ...patch });

  /* the person the fit drawings already read (same answers, nothing new) */
  function person(a) {
    const P = cfg.person || {};
    const patch = { ...(P.wt && P.wt[isKid(a) ? cfg.kidWeight : a.wt]), touched: true };
    if (P.age && P.age[a.age]) patch.age = P.age[a.age];
    if (P.sl && P.sl[a.sl]) patch.pos = P.sl[a.sl];
    state.setPerson(patch);
  }

  const sub = $('[data-hc-sub]', q);
  /* 25 Oct (AR): the promise under the title gets its own small mark so it reads as a promise, not a row label */
  if (sub && W.x_sub) {
    sub.innerHTML = `<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6z"/><path d="M8.8 12.2l2.3 2.3 4.2-4.6"/></svg><span>${esc(W.x_sub)}</span>`;
    sub.hidden = false;
  }

  function paintQ() {
    const { a, ex, seen } = answers();
    const kid = isKid(a);
    $$('[data-hc-row]', q).forEach((row) => {
      const k = row.dataset.hcRow;
      row.hidden = kid && (k === 'wt' || k === 'ex');
      row.classList.toggle('is-miss', miss && k !== 'ex' && !valid(a, k) && !row.hidden);
      $$('[data-v]', row).forEach((b) => b.setAttribute('aria-pressed', String(k === 'ex' ? ex.includes(b.dataset.v) : a[k] === b.dataset.v)));
    });
    /* 26 Oct (AR go): "Show my pick" as soon as the answers it needs are in (the sheet no longer jumps to the pick) */
    $('[data-hc-show]', q).hidden = !done(a);
    /* the sun in the bedroom rises as rows are answered (24 Oct) */
    const nd = need(a);
    sheet.style.setProperty('--hc-p', String(nd.filter((x) => valid(a, x)).length / nd.length));
  }

  /* 25 Oct (AR: "busy, cluttered"; master designer + Jobs). One calm column, no box inside a box: the picture on its
     mattress-colour wash; the name (a link to its page); why it fits; the monthly price big; price · size · per night
     small; the rating in grey; then the protector as one line, the one marigold button, the trial in small words,
     the cheaper option as a plain link, Change answers. */
  function card(o, sz) {
    const pr = priced(o, sz);
    if (!pr) return '';
    const m = pr.m;
    const img = Array.isArray(m.img) && m.img[0] ? `<img src="${esc(m.img[0])}" srcset="${esc(m.img[0])} 400w, ${esc(m.img[1] || m.img[0])} 800w" sizes="320px" width="${Number(m.img[2]) || 760}" height="${Number(m.img[3]) || 320}" alt="${esc(m.name)}" decoding="async">` : '';
    const tp = o.top && cfg.topper && cfg.topper.img ? `<span class="sd-hc-tp"><img src="${esc(cfg.topper.img)}" alt="${esc(cfg.topper.name)}" width="64" height="64" loading="lazy" decoding="async"></span>` : '';
    const months = Number(cfg.emiMonths) || 0;
    const parts = String(T.emi || '').split(' · ');
    const big = months && parts[0] ? `<p class="sd-hc-emi sunday-num"><b>${esc(fill(parts[0], { amount: money(emi(pr.total, months)), months }))}</b>${parts.length > 1 ? ` <span>${esc(fill(parts.slice(1).join(' · '), { months }))}</span>` : ''}</p>` : '';
    const yrs = Number(m.yrs) || 0;
    const night = yrs && W.x_night ? fill(W.x_night, { amount: money(Math.round(pr.total / (yrs * 365) / 100) * 100), years: yrs }) : '';
    const small = `<p class="sd-hc-pr sunday-num">${[money(pr.total), sz.word, night].filter(Boolean).map(esc).join(' · ')}</p>`;
    const head = (o.ticks || [])[0] || '';
    const rate = m.rating && m.count ? `<p class="sd-hc-rate1"><span aria-hidden="true">★</span> <span class="sunday-num">${esc(m.rating)}</span> · ${esc(fill(T.revCount || '{count}', { count: m.count }))}<span class="sd-vh"> ${esc(T.stars)}</span></p>` : '';
    return `<article class="sd-hc-card is-best" style="--sd-c:${esc(m.colour)};--sd-tint:${esc(m.tint || '')}">
      <div class="sd-hc-im">${img}${tp}</div>
      <div class="sd-hc-tx"><h3><a href="${esc(goUrl(m.url, !!o.top))}" data-hc-go${o.top ? ' data-hc-pair' : ''}>${esc(pr.name)}</a></h3>${head ? `<p class="sd-hc-why">${esc(head)}</p>` : ''}
      ${big}${small}${o.top ? `<p class="sd-hc-split sunday-num">${money(pr.mp)} + ${money(pr.tp)}</p>` : ''}${rate}</div></article>`;
  }

  function remember(d, sz, pr) {
    const m = pr.m;
    state.set('bed', { w: sz.w, l: sz.l, from: 'choose' });
    if (/^[a-z0-9-]+$/.test(m.id)) state.set('mattress', m.id);
    /* the pick, the size, the answers and the persona for the cart, once per change */
    writeAttrs(false);
  }

  function paintR() {
    const s0 = answers();
    const { a, ex } = s0;
    const d = eff(decide(cfg, a, ex), s0);
    const sz = bedSize(a);
    const pr = sz && priced(d, sz);
    if (!pr) return false;
    const m = pr.m;
    const msg = fill(T.shareText, { name: pr.name, size: sz.word, price: money(pr.total), url: m.abs || m.url });
    $('[data-hc-share]', res).href = 'https://wa.me/?text=' + encodeURIComponent(msg);
    const see = esc(goUrl(m.url, !!d.top));
    const pair = d.top ? ' data-hc-pair' : '';
    /* the protector: one optional line, its price at this size (left for the visitor to tick) */
    const pp = cfg.protector && W.x_prot ? priceAt(cfg.protector.v, sz.w, sz.l) : 0;
    const pv = pp && variantAt(cfg.protector.v, sz.w, sz.l);
    const protRow = pv ? `<label class="sd-hc-p1"><input type="checkbox" data-hc-prot${prot ? ' checked' : ''}><span class="sd-hc-p1-tx">${esc(W.x_prot)}</span><span class="sd-hc-p1-pr sunday-num">+${money(pp)}</span></label>` : '';
    const canAdd = !!(m.pid && variantAt(m.v, sz.w, sz.l) && (!d.top || (cfg.topper && variantAt(cfg.topper.v, sz.w, sz.l))));
    const addTxt = fill(d.top ? W.x_add_pair : W.x_add, { size: sz.word });
    const go = canAdd && addTxt
      ? `${protRow}<button type="button" class="sd-btn sd-full sd-hc-see" data-hc-add>${esc(addTxt)}</button>`
      : `<a class="sd-btn sd-full sd-hc-see" href="${see}" data-hc-go${pair}>${esc(d.top ? T.seePair : T.see)}</a>`;
    const trial = T.nights ? `<p class="sd-hc-trial">${esc(T.nights)}</p>` : '';
    /* the cheaper option, a plain link: a tap makes it the pick (Back to the first pick undoes it) */
    const altPr = d.alt && priced(d.alt, sz);
    const cheap = altPr && W.x_cheaper ? `<button type="button" class="sd-hc-cheap" data-hc-pick-low>${W[d.alt.why] ? `<small>${esc(W[d.alt.why])}</small>` : ''}<span>${esc(fill(W.x_cheaper, { name: altPr.name, price: money(altPr.total) }))} ›</span></button>` : '';
    const fm = d.first && mat(d.first.p);
    /* 26 Oct (AR go): the current answers in one line, Change beside them (the chips' own words, in row order) */
    const kid0 = isKid(a);
    const said = $$('[data-hc-row]', q).flatMap((row) => {
      const k = row.dataset.hcRow;
      if (kid0 && (k === 'wt' || k === 'ex')) return [];
      const on = k === 'ex' ? ex : a[k] ? [a[k]] : [];
      /* 26 Oct (AR go, old man): age and weight are bare numbers, so each carries its unit after it ("36–59 yrs",
         "80–100 kg"): the weight's from its row name ("Weight (kg)"), the age's from the language file */
      const lg = ($('legend', row)?.textContent || '').replace(/\s+/g, ' ').trim();
      const unit = k === 'wt' ? (/\(([^)]+)\)/.exec(lg) || [])[1] || '' : k === 'age' ? T.ageUnit || '' : '';
      return $$('[data-v]', row).filter((b) => on.includes(b.dataset.v)).map((b) => {
        const v = ($('span', b) || b).textContent.trim();
        return unit && !/[a-z]/i.test(v) ? `${v} ${unit}` : v;
      });
    }).filter(Boolean);
    /* 26 Oct (AR go): the answers at the foot, one quiet line with Change beside them, no box: the pick comes first */
    const ans = said.length ? `<p class="sd-hc-ans"><span>${T.answers ? `<b>${esc(T.answers)}:</b> ` : ''}${said.map(esc).join(' · ')}</span> <button type="button" class="sd-hc-lnk" data-hc-back>${esc(T.changeShort || T.change)} ›</button></p>` : '';
    const links = [
      said.length ? '' : `<button type="button" class="sd-hc-lnk" data-hc-back>${esc(T.change)} ›</button>`,
      fm && T.firstPick ? `<button type="button" class="sd-hc-lnk" data-hc-first>${esc(fill(T.firstPick, { name: fm.short || fm.name }))} ›</button>` : '',
    ].filter(Boolean);
    $('[data-hc-body]', res).innerHTML = `${card(d, sz)}${go}${trial}${cheap}${ans}${links.length ? `<p class="sd-hc-links">${links.join('')}</p>` : ''}`;
    remember(d, sz, pr);
    /* the cart module, ready before the tap (small; usually already loaded by the header) */
    if (canAdd) import('@sunday/cart').catch(() => {});
    return true;
  }

  /* "Add <size> to cart": the pick (and its topper, and the protector when ticked) in one call, as one bed; then the cart */
  async function addPick(b) {
    if (b.disabled) return;
    const s0 = answers();
    const d = eff(decide(cfg, s0.a, s0.ex), s0);
    const sz = bedSize(s0.a);
    const m = mat(d.p);
    const vid = m && sz && variantAt(m.v, sz.w, sz.l);
    if (!vid || !m.pid) return;
    const also = [];
    if (d.top && cfg.topper) {
      const t = variantAt(cfg.topper.v, sz.w, sz.l);
      if (t) also.push(t);
    }
    const pc = $('[data-hc-prot]', res);
    if (pc && pc.checked && cfg.protector) {
      const p = variantAt(cfg.protector.v, sz.w, sz.l);
      if (p) also.push(p);
    }
    const was = b.textContent;
    b.disabled = true;
    b.setAttribute('aria-busy', 'true');
    if (W.x_adding) b.textContent = W.x_adding;
    try {
      const cart = await import('@sunday/cart');
      await cart.addFromProductPage({ variantId: vid, productId: m.pid, size: { w: sz.w, l: sz.l }, also, go: true });
      const r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      location.href = r + 'cart';
    } catch (e) {
      b.disabled = false;
      b.removeAttribute('aria-busy');
      b.textContent = W.x_fail || was;
    }
  }

  /* 25 Oct (AR): every "Not sure? Help me choose" link (data-sd-hmc) says the pick once the answers are in:
     "Ortho Latex 4 is your pick · Change answers ›"; a tap opens the result again. The › stays only where the link
     had its own (some links draw it with CSS). */
  function paintLinks(pk) {
    const w = T.hmcPick;
    if (!w) return;
    $$('a[data-sd-hmc][href="#build"]').forEach((a) => {
      if (a.dataset.hmcDef == null) a.dataset.hmcDef = a.textContent.trim();
      const def = a.dataset.hmcDef;
      let t = pk ? fill(w, { name: pk.trim || pk.name }) : def;
      if (pk && !/›\s*$/.test(def)) t = t.replace(/\s*›\s*$/, '');
      if (a.textContent !== t) a.textContent = t;
      a.classList.toggle('sd-hmc-on', !!pk);
    });
  }

  function paintPill() {
    const pk = current();
    paintLinks(pk);
    /* 26 Oct (AR go, no flash): the next page writes the pick into its links and tiles before it shows
       (snippets/sunday-pick-early.liquid reads this) */
    if (cfg) try { if (pk) localStorage.setItem('sunday-pick', JSON.stringify({ id: pk.id, n: pk.trim || pk.name })); else localStorage.removeItem('sunday-pick'); } catch (e) {}
    const label = pk ? fill(T.yourPick, { name: pk.name }) : T.title; /* full name: "Your pick: Ortho Latex 4 Mattress" */
    [pill, $('.sd-dock [data-sd-pill-open]')].forEach((b) => {
      if (!b) return;
      const l = $('[data-sd-dock-label], [data-hc-pill-label]', b);
      const dot = $('[data-hc-dot]', b);
      if (l) {
        l.textContent = label;
        /* the full name, unless it would take a second line in this language: then the short name */
        if (pk && pk.short && l.getClientRects().length > 1) l.textContent = fill(T.yourPick, { name: pk.short });
      }
      if (dot) {
        dot.hidden = !pk;
        if (pk) dot.style.setProperty('--hc-c', pk.colour);
      }
      b.classList.toggle('has-pick', !!pk);
      const ic = $('[data-hc-ic]', b);
      if (ic) ic.hidden = !!pk;
    });
  }

  function paint() {
    const { a } = answers();
    const showR = scr === 'r' && done(a);
    q.hidden = showR;
    res.hidden = !showR;
    if (showR && !paintR()) {
      q.hidden = false;
      res.hidden = true;
    } else if (showR && open) finished();
    paintQ();
    paintPill();
    root.classList.toggle('is-open', open);
    oneMari();
    if (typeof place === 'function') place();
  }

  /* From 960 the panel sits over the page without a dim: its button turns navy while another marigold
     button is on screen (one marigold per screen). */
  function oneMari() {
    $$('.sd-hc-see, [data-hc-show]', sheet).forEach((b) => b.classList.remove('sd-nv'));
    if (!open || !wide()) return;
    $$('.sd-hc-see, [data-hc-show]', sheet).forEach((b) => {
      if (b.hidden || !b.offsetParent) return;
      const mari = getComputedStyle(b).backgroundColor;
      const other = $$('.sd-btn').some((x) => {
        if (root.contains(x) || !x.offsetParent) return false;
        const r = x.getBoundingClientRect();
        return r.bottom > 0 && r.top < innerHeight && r.width > 0 && getComputedStyle(x).backgroundColor === mari;
      });
      b.classList.toggle('sd-nv', other);
    });
  }

  let lastId;
  function announce() {
    const pk = current();
    const id = pk ? pk.id + (pk.top ? '+t' : '') : null;
    if (id === lastId) return;
    const first = lastId === undefined;
    lastId = id;
    moveTag(pk);
    paintLinks(pk);
    if (!first || pk) document.dispatchEvent(new CustomEvent('sunday:pick', { detail: pk }));
  }

  /* 29 Oct (AR go): survey_finish once per set of answers (kept for this tab, so a reload showing the same result sends nothing) */
  function finished() {
    const s = answers();
    const pk = current();
    if (!pk) return;
    const pe = personaOf(cfg, s.a, s.ex);
    const k = tag(keyOf(s.a, s.ex) + '|' + pk.code + '|' + (pe ? pe.code : ''));
    let was = lastFin;
    try { was = was || sessionStorage.getItem('sd-hc-fin') || ''; } catch (e) { /* storage blocked */ }
    if (k === was) return;
    lastFin = k;
    try { sessionStorage.setItem('sd-hc-fin', k); } catch (e) { /* storage blocked */ }
    ga('survey_finish', { pick: pk.code, persona: pe ? pe.code : '' });
  }

  function openSheet(trigger) {
    if (!open) ga('survey_open', {});
    from = trigger || null;
    const { a, seen } = answers();
    scr = seen && done(a) ? 'r' : 'q';
    open = true;
    sheet.hidden = false;
    dim.hidden = wide();
    html.classList.add('sd-hc-open');
    if (!wide()) html.style.overflow = 'hidden';
    $$('[data-sd-pill-open], [data-hc-pill]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    paint();
    sheet.scrollTop = 0;
    /* 24 Oct (AR): focus goes to the sheet itself, not Close, so Close never opens with a focus ring round it */
    sheet.focus({ preventScroll: true });
  }
  function closeSheet(back = true) {
    if (!open) return;
    open = false;
    sheet.hidden = true;
    dim.hidden = true;
    html.classList.remove('sd-hc-open');
    html.style.overflow = '';
    $$('[data-sd-pill-open], [data-hc-pill]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    paint();
    if (back && from && document.contains(from) && from.offsetParent !== null) from.focus({ preventScroll: true });
  }

  root.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('[data-hc-prot]')) prot = e.target.checked;
  });
  root.addEventListener('click', (e) => {
    const b = e.target.closest('button, a');
    if (!b) return;
    if (b.matches('[data-hc-x]')) return void closeSheet();
    if (b.matches('[data-hc-pill]')) {
      /* 24 Oct (AR): with a pick, the button opens the result again (answers can be changed there) */
      return void (open ? closeSheet() : openSheet(b));
    }
    if (b.matches('[data-hc-add]')) { if (Date.now() - autoAt < 700) return; return void addPick(b); }
    if (b.matches('[data-hc-back]')) {
      scr = 'q';
      return void paint();
    }
    if (b.matches('[data-hc-first]')) {
      save({ low: '' });
      paint();
      sheet.scrollTop = 0;
      return;
    }
    if (b.matches('[data-hc-pick-low]')) {
      const s = answers();
      save({ low: keyOf(s.a, s.ex) });
      paint();
      sheet.scrollTop = 0;
      return;
    }
    if (b.matches('[data-hc-show]')) {
      save({ seen: true });
      scr = 'r';
      paint();
      sheet.scrollTop = 0;
      return;
    }
    if (b.matches('[data-hc-go]')) {
      closeSheet(false);
      /* the pick is this page: no reload, just the buy area (the size follows through "bed") */
      if (new URL(b.href, location.href).pathname === location.pathname && $('#add')) {
        e.preventDefault();
        /* the pair: the page ticks the topper beside Add */
        if (b.matches('[data-hc-pair]')) document.dispatchEvent(new CustomEvent('sunday:pair'));
        toBuy();
      }
      return;
    }
    const row = b.closest('[data-hc-row]');
    if (!row || !b.dataset.v) return;
    const k = row.dataset.hcRow;
    const v = b.dataset.v;
    const s = answers();
    if (k === 'ex') {
      save({ ex: s.ex.includes(v) ? s.ex.filter((x) => x !== v) : [...s.ex, v] });
    } else {
      const a = { ...s.a, [k]: v };
      const patch = { a };
      if (k === 'sz') {
        if (done(a)) {
          scr = 'r';
          miss = false;
          patch.seen = true;
          autoAt = Date.now();
        } else miss = true;
      } else if (!s.seen && done(a)) {
        scr = 'r';
        miss = false;
        patch.seen = true;
        autoAt = Date.now();
      }
      /* 29 Oct (AR go): the pick opens by itself again on the answer that completes the survey (size already known); taps on Add are ignored for 700 ms after it opens so the next tap can't add a mattress by mistake. */
      save(patch);
      if (a.age && a.sl && (a.wt || isKid(a))) person(a);
    }
    paint();
    if (scr === 'r') sheet.scrollTop = 0;
  });
  dim.addEventListener('click', () => closeSheet());
  document.addEventListener('keydown', (e) => {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeSheet();
    } else if (e.key === 'Tab' && !wide()) {
      const f = $$('button:not([disabled]), a[href], input', sheet).filter((x) => x.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) {
        e.preventDefault();
        f[f.length - 1].focus();
      } else if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
        e.preventDefault();
        f[0].focus();
      }
    }
  });
  /* desktop: a tap outside the panel closes it */
  document.addEventListener('click', (e) => {
    if (!open || e.__sdHc || !wide()) return;
    if (e.target.closest('[data-hc-sheet], [data-hc-pill], [data-sd-pill-open]')) return;
    closeSheet(false);
  });

  /* every old entry point opens it: "#build" links, the dock's button, the old Build your bed address */
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target.closest && e.target.closest('a[href="#build"], a[href$="#build"], a[href*="build-your-bed"], [data-sd-pill-open]');
      if (!t || e.defaultPrevented) return;
      e.preventDefault();
      e.__sdHc = true;
      const menu = $('#ov');
      if (menu && !menu.hidden) {
        const x = $('[data-close]', menu);
        if (x) x.click();
      }
      if (open && t.matches('[data-sd-pill-open]')) closeSheet();
      else openSheet(t);
    },
    true
  );
  matchMedia('(min-width: 960px)').addEventListener('change', () => open && closeSheet(false));
  /* 25 Oct (AR go): Build your bed is retired; its old address redirects to /collections/…#build, and any page
     opened with #build (menu, persona and old links) opens Find your mattress straight away */
  if (location.hash === '#build') {
    try {
      history.replaceState(null, '', location.pathname + location.search);
    } catch (e) {
      /* keep going */
    }
    const go = () => setTimeout(() => openSheet(null), 60);
    if (document.readyState === 'complete') go();
    else addEventListener('load', go, { once: true });
  }

  /* 26 Oct (AR go: "it just ends up flickering while scrolling"): the desktop pill holds still while the page moves.
     Once scrolling stops it checks one time and fades only while it would sit on a button, field or price (not on
     plain words). The header, the dock and the sheet are left out. */
  const UNDER = 'a.sd-btn, button, summary, input:not([type=hidden]), select, textarea, [data-add], [data-price], .sd-price, .sd-ft';
  const near = (r, pr) => r.width > 0 && r.height > 0 && r.left < pr.right + 6 && r.right > pr.left - 6 && r.top < pr.bottom + 6 && r.bottom > pr.top - 6;
  let raf = 0;
  function place() {
    if (!pill || !matchMedia('(min-width: 720px)').matches) return;
    let hit = false;
    if (!open) {
      root.classList.remove('is-under'); /* measured where it sits when shown; no paint happens in between */
      const pr = pill.getBoundingClientRect();
      const skip = (el) => root.contains(el) || el.closest('.sd-dock, #ov, [hidden], header');
      if (pr.width) hit = $$(UNDER).some((el) => !skip(el) && near(el.getBoundingClientRect(), pr));
    }
    root.classList.toggle('is-under', hit);
  }
  let still = 0;
  const again = () => {
    clearTimeout(still);
    still = setTimeout(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        place();
        if (open) oneMari();
      });
    }, 250);
  };
  addEventListener('scroll', again, { passive: true });
  addEventListener('resize', again);
  const pw = $('.page-wrapper');
  if (pw) pw.addEventListener('scroll', again, { passive: true });

  document.addEventListener('sunday:state', (e) => {
    const k = e.detail && e.detail.key;
    if (k === 'choose') announce();
  });
  paint();
  announce();
  place();
  document.fonts?.ready.then(paintPill); /* the language's own font can make the label longer once it loads */
}

/* ---------- the Home tiles: the pick moves first with its tag (as on Mattresses); no pick, the page's own order ---------- */
function moveTag(pk) {
  const tiles = $$('.sd-mtile[data-handle]');
  if (!tiles.length) return;
  const row = tiles[0].closest('[data-pick-tag]');
  const pickWord = (row && row.dataset.pickTag) || '';
  const defWord = (row && row.dataset.defaultTag) || '';
  if (row && !homeOrder) homeOrder = tiles.slice();
  tiles.forEach((t) => $$('.sd-mttag', t).forEach((x) => x.remove()));
  tiles.forEach((t) => t.classList.toggle('sd-mtile--pk', !!(pk && t.dataset.handle === pk.id)));
  const pkTile = pk && tiles.find((t) => t.dataset.handle === pk.id);
  /* 30 Oct (AR go 2): one outlined tile: the pick's, else Most chosen */
  /* 31 Oct (AR go 4): outlined only when it is the visitor's own pick */
  tiles.forEach((t) => t.toggleAttribute('data-hi', !!pkTile && t === pkTile));
  if (row && homeOrder) {
    const first = row.firstElementChild;
    const order = pkTile ? [pkTile, ...homeOrder.filter((t) => t !== pkTile)] : homeOrder;
    if (order[0] !== first || pkTile) {
      order.forEach((t) => row.appendChild(t));
      /* phone: the scroller starts on the pick */
      row.scrollLeft = 0;
    }
  }
  /* 30 Oct (AR go): every tile keeps its own tag (data-eb: Luxury · Most chosen · Our firmest); the pick's says "Your pick" */
  tiles.forEach((t) => {
    const mine = pkTile && t === pkTile;
    const hit = mine || t.dataset.eb != null ? true : (!pkTile && t.hasAttribute('data-default-tag'));
    const word = mine ? pickWord : (t.dataset.eb != null ? t.dataset.eb : defWord);
    if (hit && word) {
      const s = document.createElement('span');
      s.className = mine ? 'sd-mttag sd-mttag--pk' : 'sd-mttag';
      s.textContent = word;
      const h = t.querySelector('h3');
      if (h) h.appendChild(s); else t.insertBefore(s, t.firstChild);
    }
  });
}

/* ---------- from the result to a mattress page: land with the price and Add in view ----------
   No smooth scrolling (it does not run in a hidden tab and lands short). The place is checked again while
   the page is still growing (images, fonts, late parts) for up to 3 s, until the visitor scrolls. */
function toBuy() {
  const add = $('#add');
  if (!add) return;
  const pw = $('.page-wrapper');
  const inner = () => pw && pw.scrollHeight > pw.clientHeight + 1 && getComputedStyle(pw).overflowY !== 'visible';
  const hdr = $('[data-sunday-header]');
  const put = () => {
    const r = add.getBoundingClientRect();
    if (!r.height) return;
    const pr = $('sunday-m-hero [data-price]');
    const top = pr && pr.getBoundingClientRect().height ? Math.min(pr.getBoundingClientRect().top, r.top) : r.top;
    const sp = $('.sd-spine');
    const head = Math.max(0, hdr ? hdr.getBoundingClientRect().bottom : 0, sp && getComputedStyle(sp).position === 'sticky' ? sp.getBoundingClientRect().bottom : 0);
    const vh = innerHeight;
    /* Add's top at the set share of the screen, the price under the header, and Add's bottom 16 px clear of the edge (Add wins) */
    let y = r.top - vh * Number(cfg.landAt || 0.6);
    y = Math.min(y, top - head - 12);
    y = Math.max(y, r.bottom + 16 - vh);
    if (Math.abs(y) < 2) return;
    if (inner()) pw.scrollTo({ top: pw.scrollTop + y, behavior: 'instant' });
    else scrollTo({ top: scrollY + y, behavior: 'instant' });
  };
  put();
  let stop = false;
  const end = () => (stop = true);
  ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((t) => addEventListener(t, end, { once: true, passive: true }));
  const ro = 'ResizeObserver' in window ? new ResizeObserver(() => !stop && put()) : null;
  if (ro) [document.body, $('main'), add.closest('sunday-m-hero')].forEach((el) => el && ro.observe(el));
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => !stop && put());
  setTimeout(() => {
    stop = true;
    if (ro) ro.disconnect();
  }, 3000);
}
if (cfg && cfg.land && (location.hash === '#' + cfg.land || pairIn)) {
  const land = () => {
    try {
      history.replaceState(null, '', location.pathname + location.search);
    } catch (e) {
      /* keep going */
    }
    toBuy();
  };
  if (document.readyState === 'complete') setTimeout(land, 60);
  else addEventListener('load', () => setTimeout(land, 60), { once: true });
}
